/**
 * A safe storage utility that automatically handles:
 * - sessionStorage / localStorage fallback
 * - JSON parsing/stringifying
 * - Expiration timestamps
 * - Safari private mode exceptions
 */

type StorageType = "local" | "session";

interface SetStorageOptions {
  type?: StorageType; // default: session
  ttlMs?: number; // time-to-live in milliseconds
}

const isStorageAvailable = (type: StorageType): boolean => {
  try {
    const storage = type === "local" ? localStorage : sessionStorage;
    const testKey = "__test__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const getStorage = (type: StorageType = "session"): Storage | null => {
  if (isStorageAvailable(type)) {
    return type === "local" ? localStorage : sessionStorage;
  }
  return null;
};

export const storageUtil = {
  /**
   * Sets a value in storage (handles objects & TTL).
   */
  set(key: string, value: any, options?: SetStorageOptions) {
    const storage = getStorage(options?.type);
    if (!storage) return;

    const data = {
      value,
      expiry: options?.ttlMs ? Date.now() + options.ttlMs : null,
    };

    try {
      storage.setItem(key, JSON.stringify(data));
    } catch (err) {
      console.warn(`Failed to set storage key "${key}":`, err);
    }
  },

  /**
   * Gets a value from storage, respecting TTL if present.
   */
  get<T = any>(key: string, type: StorageType = "session"): T | null {
    const storage = getStorage(type);
    if (!storage) return null;

    try {
      const raw = storage.getItem(key);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (data.expiry && Date.now() > data.expiry) {
        storage.removeItem(key);
        return null;
      }
      return data.value as T;
    } catch (err) {
      console.warn(`Failed to parse storage key "${key}":`, err);
      return null;
    }
  },

  /**
   * Removes a key from storage.
   */
  remove(key: string, type: StorageType = "session") {
    const storage = getStorage(type);
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch (err) {
      console.warn(`Failed to remove storage key "${key}":`, err);
    }
  },
};

/*
Usage Example:
Set item (1-day TTL) - storageUtil.set("isPrivateMode", true, { type: "session", ttlMs: 24 * 60 * 60 * 1000 });
Get item - const isPrivate = storageUtil.get<boolean>("isPrivateMode");
Remove item - storageUtil.remove("isPrivateMode");
*/